import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { MdOutlineAddCard } from "react-icons/md";
import debounce from "lodash/debounce";
import useApi from "../../hook/useApi";
import Loader from "../Loader/Loader";
import Paggination from "../Paggination";
import { PAGE_SIZE_OPTIONS } from "../../constants/pageSizeOptions";
import SearchableSelect from "../Common/SearchableSelect";

const SegmentListLayer = () => {
  const { AsyncGetApiCall, AsyncPostApiCall, AsyncPatchApiCall, AsyncDeleteApiCall } = useApi();
  const [loading, setLoading] = useState(false);
  const [segments, setSegments] = useState([]);
  const [query, setQuery] = useState({
    pageNo: 1,
    limitVal: 10,
    search: "",
  });
  const [meta, setMeta] = useState({
    total: 0,
    totalPages: 1,
    currentPage: 1,
  });
  const [searchInput, setSearchInput] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    displayName: "",
    isActive: true,
  });

  const fetchSegments = async () => {
    try {
      setLoading(true);
      const result = await AsyncGetApiCall(
        `/trading-settings/segments/list?search=${encodeURIComponent(
          query.search || "",
        )}&pageNo=${query.pageNo}&limitVal=${query.limitVal}`,
      );
      if (result?.success) {
        setSegments(Array.isArray(result.data) ? result.data : []);
        setMeta({
          total: result.total ?? 0,
          totalPages: Math.max(1, result.totalPages ?? 1),
          currentPage: result.currentPage ?? query.pageNo,
        });
      } else {
        setSegments([]);
        setMeta({ total: 0, totalPages: 1, currentPage: 1 });
      }
    } catch (error) {
      console.error("Error fetching segments:", error);
      setSegments([]);
      setMeta({ total: 0, totalPages: 1, currentPage: 1 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSegments();
  }, [query.pageNo, query.limitVal, query.search]);

  const debouncedSearch = useCallback(
    debounce((value) => {
      setQuery((prev) => ({ ...prev, search: value, pageNo: 1 }));
    }, 500),
    [],
  );

  useEffect(
    () => () => {
      debouncedSearch.cancel();
    },
    [debouncedSearch],
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSearch(value);
  };

  const safePage = Math.min(query.pageNo, Math.max(1, meta.totalPages));

  const handleOpenModal = (data = null) => {
    if (data) {
      setEditData(data);
      setFormData({
        name: data.name,
        displayName: data.displayName,
        isActive: data.isActive,
      });
    } else {
      setEditData(null);
      setFormData({ name: "", displayName: "", isActive: true });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditData(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let result;
      if (editData) {
        result = await AsyncPatchApiCall(`/trading-settings/segments/update/${editData._id}`, formData);
      } else {
        result = await AsyncPostApiCall("/trading-settings/segments/create", formData);
      }

      if (result) {
        toast.success(editData ? "Segment updated" : "Segment created");
        handleCloseModal();
        fetchSegments();
      }
    } catch (error) {
      toast.error("Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this segment?")) return;
    setLoading(true);
    try {
      const result = await AsyncDeleteApiCall(`/trading-settings/segments/delete/${id}`);
      if (result) {
        toast.success("Segment deleted");
        fetchSegments();
      }
    } catch (error) {
      toast.error("Delete failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Loader />}
      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center justify-content-between gap-3 w-100">
            <div className="d-flex align-items-center gap-3">
              <span className="text-md fw-medium text-secondary-light mb-0">
                Show
              </span>
              <div style={{ minWidth: "92px" }}>
                <SearchableSelect
                  placeholder="Show"
                  value={String(query.limitVal)}
                  showSearch={false}
                  onChange={(e) =>
                    setQuery((prev) => ({
                      ...prev,
                      limitVal: parseInt(e.target.value, 10),
                      pageNo: 1,
                    }))
                  }
                  options={PAGE_SIZE_OPTIONS.map((n) => ({
                    label: String(n),
                    value: String(n),
                  }))}
                />
              </div>
              <form className="navbar-search" onSubmit={(e) => e.preventDefault()}>
                <input
                  type="text"
                  className="bg-base h-40-px w-auto"
                  placeholder="Search"
                  value={searchInput}
                  onChange={handleSearchChange}
                />
                <Icon icon="ion:search-outline" className="icon" />
              </form>
            </div>
          <button
            type="button"
            className="btn btn-primary-600 radius-8 px-17 py-9 d-flex align-items-center gap-2"
            onClick={() => handleOpenModal()}
          >
            <MdOutlineAddCard className="text-xl" />
            Add Segment
          </button>
          </div>
        </div>

        <div className="card-body p-24">
          <div className="table-responsive scroll-sm">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th scope="col">S.No</th>
                  <th scope="col">Name</th>
                  <th scope="col">Display Name</th>
                  <th scope="col" className="text-center">Status</th>
                  <th scope="col" className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {segments.length > 0 ? (
                  segments.map((segment, index) => (
                    <tr key={segment._id}>
                      <td>{(safePage - 1) * query.limitVal + index + 1}</td>
                      <td>{segment.name}</td>
                      <td>{segment.displayName}</td>
                      <td className="text-center">
                        <span className={`badge ${segment.isActive ? "bg-success-focus text-success-600" : "bg-danger-focus text-danger-600"}`}>
                          {segment.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center gap-2 justify-content-center">
                          <button
                            className="bg-success-focus text-success-600 w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle border-0"
                            onClick={() => handleOpenModal(segment)}
                          >
                            <Icon icon="lucide:edit" />
                          </button>
                          <button
                            className="bg-danger-focus text-danger-600 w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle border-0"
                            onClick={() => handleDelete(segment._id)}
                          >
                            <Icon icon="mdi:delete-outline" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center">No segments found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Paggination
            total={meta.total}
            totalPages={meta.totalPages}
            currentPage={meta.currentPage}
            setPageNo={(page) =>
              setQuery((prev) => ({ ...prev, pageNo: page }))
            }
            startFrom={meta.total > 0 ? (meta.currentPage - 1) * query.limitVal + 1 : 0}
            endTo={meta.total > 0 ? (meta.currentPage - 1) * query.limitVal + segments.length : 0}
          />
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editData ? "Edit Segment" : "Add Segment"}</h5>
                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Internal Name (e.g. NFO)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Display Name (e.g. NSE Futures)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.displayName}
                      onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                    <label className="form-check-label">Active</label>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>Cancel</button>
                  <button type="submit" className="btn btn-primary">{editData ? "Update" : "Save"}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SegmentListLayer;
